import type { OMSConfig, StrategyEvaluator, SignalResult, StrategyContext } from '../types';
import { ensureFlatOpts } from '../../analysis/trend-evaluator';
import { find0916Snapshot, extractSnapshotOptions } from '../../../lib/snapshot-search';

/**
 * ConnectingDotsBreakout - Matches the 'Breakout' dot in ConnectingDotsWidget.tsx
 * 
 * Logic:
 * 1. Takes top 5 ITM strikes from 09:16 snapshot
 * 2. Compares against live top 5 ITM strikes
 * 3. Bullish if 09:16 min strike dropped out AND live min > 09:16 min
 * 4. Bearish if 09:16 max strike dropped out AND live max < 09:16 max
 */
export const evaluateConnectingDotsBreakout: StrategyEvaluator = (data: any, currentUnixTime: number, config: OMSConfig, context: StrategyContext): SignalResult | null => {
    if (!data) return null;

    const { symbol, expiry, activeRange, gap, settingsMode, baseSnapshot } = context;
    const spot = data.spotPrice || data.index_ltp || data.ltp || 0;
    if (spot <= 0) return null;

    // 1. Get 09:16 Snapshot Top 5
    const snap0916 = baseSnapshot;
    if (!snap0916) return null;
    
    const opts0916 = extractSnapshotOptions(snap0916, symbol, expiry);
    if (!opts0916 || opts0916.length === 0) return null;

    const atm0916 = snap0916.atmStrike || (Math.round((snap0916.spotPrice || spot) / gap) * gap);
    const minStrike0916 = atm0916 - activeRange * gap;
    const maxStrike0916 = atm0916 + activeRange * gap;

    const cellDataKeys: Record<string, string> = {
        'Combined OI': 'combOI',
        'Combined Volume': 'combVol',
        'Top OI': 'oi',
        'Top Volume': 'volume',
        'Top Chng OI': 'oiChg',
    };
    const valKey = cellDataKeys[settingsMode] || 'volume';

    const getSortKey = (row: any) => {
        return Number(row[valKey]) || 0;
    };

    const allRows0916 = ensureFlatOpts(opts0916)
        .filter((o: any) => o.strike >= minStrike0916 && o.strike <= maxStrike0916);

    const itmRows0916 = allRows0916.filter((r: any) => r.type === 'CE' ? r.strike <= atm0916 : r.strike >= atm0916);
    itmRows0916.sort((a: any, b: any) => getSortKey(b) - getSortKey(a));
    const top5Itm0916 = itmRows0916.slice(0, 5);
    const strikes0916 = top5Itm0916.map((r: any) => r.strike);
    
    if (strikes0916.length === 0) return null;
    
    const min0916 = Math.min(...strikes0916);
    const max0916 = Math.max(...strikes0916);

    // 2. Get Live Top 5
    const liveRawOpts = context.chain || extractSnapshotOptions(data, symbol, expiry) || [];
    if (!liveRawOpts || liveRawOpts.length === 0) return null;

    const atmLive = Math.round(spot / gap) * gap;
    const minStrikeLive = atmLive - activeRange * gap;
    const maxStrikeLive = atmLive + activeRange * gap;

    const allRowsLive = ensureFlatOpts(liveRawOpts)
        .filter((o: any) => o.strike >= minStrikeLive && o.strike <= maxStrikeLive);

    const itmRowsLive = allRowsLive.filter((r: any) => r.type === 'CE' ? r.strike <= atmLive : r.strike >= atmLive);
    itmRowsLive.sort((a: any, b: any) => getSortKey(b) - getSortKey(a));
    const top5ItmLive = itmRowsLive.slice(0, 5);
    const liveStrikes = top5ItmLive.map((r: any) => r.strike);
    
    if (liveStrikes.length === 0) return null;

    const liveMin = Math.min(...liveStrikes);
    const liveMax = Math.max(...liveStrikes);

    // 3. Compare for Breakout
    const hasMin = liveStrikes.includes(min0916);
    const hasMax = liveStrikes.includes(max0916);

    const isBullish = !hasMin && liveMin > min0916;
    const isBearish = !hasMax && liveMax < max0916;

    let status = 'Sideways';
    if (isBullish && !isBearish) status = 'BULL';
    else if (isBearish && !isBullish) status = 'BEAR';

    if (status === 'Sideways') return null;

    // 4. Entry Strike
    const optionType = status === 'BULL' ? 'CE' : 'PE';
    let entryPrice = 0;
    
    const atmRow = allRowsLive.find(r => r.strike === atmLive && r.type === optionType);
    if (atmRow) {
        entryPrice = atmRow.ltp;
    }
    
    if (entryPrice <= 0) return null;

    return {
        trade_type: status as any,
        strike: atmLive,
        option_type: optionType,
        entry_price: entryPrice,
        remarks: `Breakout (0916: ${min0916}-${max0916} ➔ Live: ${liveMin}-${liveMax})`
    };
};
